/*
 * WSS width guard
 * ----------------------------------------------------------------------------
 * Product pages used to open with the first in-stock width (6mm) already selected, so shoppers could add the wrong
 * size without noticing. When a product page (or quick buy) is opened without a specific variant in the URL:
 *   1. If the shopper chose a width recently (on any strap), that width is selected for them.
 *   2. Otherwise no width is selected, the width label reads "Select a width", and Add to cart asks them to choose one
 *      instead of adding to the cart. Buy-now / express checkout buttons are hidden until a width is chosen.
 * Pages opened with ?variant= (width collections, Google Shopping, ads) behave exactly as before.
 *
 * Markup hook: <wss-width-guard form-id="..." option-position="1" no-variant> rendered in snippets/variant-picker.liquid.
 * Impact re-renders the product section on every option change, so the guard reconnects each time; the per-form state
 * below survives those re-renders.
 */
(function () {
  if (window.customElements.get('wss-width-guard')) return;

  var STORAGE_KEY = 'wss:width';
  var REMEMBER_FOR = 30 * 24 * 60 * 60 * 1000;
  var PROMPT_LABEL = 'Select a width';
  var BUTTON_LABEL = 'Select a width';
  var MESSAGE = 'Please choose a width to continue.';

  var forms = {}; // formId -> { pending: boolean, position: string }

  var style = document.createElement('style');
  style.textContent =
    '.wss-width-pending .shopify-payment-button{display:none!important}' +
    '.wss-width-label{font-weight:600}' +
    '.wss-width-hide{display:none!important}' +
    '.wss-width-alert{animation:wss-width-flash 1.2s ease-out 1}' +
    '@keyframes wss-width-flash{0%,40%{background:rgba(198,40,40,.08);box-shadow:0 0 0 6px rgba(198,40,40,.08)}100%{background:transparent;box-shadow:0 0 0 6px transparent}}' +
    '.wss-width-msg{color:#b3261e;font-size:.875rem;margin-top:.5rem}' +
    '@media (prefers-reduced-motion: reduce){.wss-width-alert{animation:none}}';
  document.head.appendChild(style);

  function readRememberedWidth() {
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && saved.w && Date.now() - saved.t < REMEMBER_FOR) return saved.w;
    } catch (e) {}
    return null;
  }

  function rememberWidth(width) {
    if (!width) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ w: width, t: Date.now() })); } catch (e) {}
  }

  function widthInputs(formId, position) {
    return Array.prototype.slice.call(
      document.querySelectorAll('input[form="' + formId + '"][data-option-position="' + position + '"]')
    );
  }

  function labelFor(input) {
    return document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
  }

  function widthName(input) {
    var label = labelFor(input);
    return label ? label.textContent.trim() : '';
  }

  function widthFieldset(formId, position) {
    var first = widthInputs(formId, position)[0];
    return first ? first.closest('fieldset') : null;
  }

  function buyButtons(formId) {
    return Array.prototype.slice.call(document.querySelectorAll('buy-buttons[form="' + formId + '"]'));
  }

  function submitLabel(buyButton) {
    var button = buyButton.querySelector('button[type="submit"]');
    return button ? button.querySelector(':scope > div') || button : null;
  }

  function applyPending(formId) {
    var entry = forms[formId];
    if (!entry || !entry.pending) return;

    widthInputs(formId, entry.position).forEach(function (input) { input.checked = false; });

    var fieldset = widthFieldset(formId, entry.position);
    var valueLabel = fieldset && fieldset.querySelector('.variant-picker__option-info span');
    if (valueLabel) {
      valueLabel.textContent = PROMPT_LABEL;
      valueLabel.classList.add('wss-width-label');
    }

    // Stock messages ("Only 4 units left") and the back-in-stock sign-up describe whichever width the theme picked,
    // so hide them until one is chosen.
    var scope = (fieldset && fieldset.closest('.product-info')) || null;
    if (scope) {
      scope.querySelectorAll('[data-block-type="inventory"], .wss-restock').forEach(function (el) { el.classList.add('wss-width-hide'); });
    }

    buyButtons(formId).forEach(function (buyButton) {
      buyButton.classList.add('wss-width-pending');
      var label = submitLabel(buyButton);
      if (label && label.textContent.trim() !== BUTTON_LABEL) {
        if (!label.hasAttribute('data-wss-original')) label.setAttribute('data-wss-original', label.textContent);
        label.textContent = BUTTON_LABEL;
      }
    });
  }

  function clearPending(formId) {
    buyButtons(formId).forEach(function (buyButton) {
      buyButton.classList.remove('wss-width-pending');
      var label = submitLabel(buyButton);
      if (label && label.hasAttribute('data-wss-original')) {
        label.textContent = label.getAttribute('data-wss-original');
        label.removeAttribute('data-wss-original');
      }
    });
    document.querySelectorAll('.wss-width-msg[data-form="' + formId + '"]').forEach(function (el) { el.remove(); });
    document.querySelectorAll('.wss-width-hide').forEach(function (el) { el.classList.remove('wss-width-hide'); });
  }

  function promptForWidth(formId) {
    var entry = forms[formId];
    var fieldset = widthFieldset(formId, entry.position);
    if (!fieldset) return;

    if (!fieldset.querySelector('.wss-width-msg')) {
      var message = document.createElement('p');
      message.className = 'wss-width-msg';
      message.setAttribute('role', 'alert');
      message.setAttribute('data-form', formId);
      message.textContent = MESSAGE;
      fieldset.appendChild(message);
    }

    fieldset.classList.remove('wss-width-alert');
    void fieldset.offsetWidth; // restart the flash animation
    fieldset.classList.add('wss-width-alert');
    fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function selectWidth(input) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  var WidthGuard = class extends HTMLElement {
    connectedCallback() {
      var formId = this.getAttribute('form-id');
      var position = this.getAttribute('option-position');
      if (!formId || !position) return;

      var entry = forms[formId];

      if (!entry) {
        entry = forms[formId] = { pending: this.hasAttribute('no-variant'), position: position };

        if (!entry.pending) {
          // Opened with a specific variant: remember that width for the next strap.
          var checked = widthInputs(formId, position).filter(function (input) { return input.checked; })[0];
          if (checked) rememberWidth(widthName(checked));
          return;
        }

        var remembered = readRememberedWidth();
        if (remembered) {
          var match = widthInputs(formId, position).filter(function (input) {
            var label = labelFor(input);
            return label && !label.classList.contains('is-disabled') && widthName(input).toLowerCase() === remembered.toLowerCase();
          })[0];

          if (match) {
            entry.pending = false;
            selectWidth(match);
            return;
          }
        }
      }

      if (entry.pending) applyPending(formId);
    }
  };

  // A width was chosen: stop guarding this form and remember the choice.
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('input[data-option-position][form]')) return;
    var formId = input.getAttribute('form');
    var entry = forms[formId];
    if (!entry || input.getAttribute('data-option-position') !== entry.position) return;

    rememberWidth(widthName(input));
    if (entry.pending) {
      entry.pending = false;
      clearPending(formId);
    }
  }, true);

  // Impact updates the buttons after each re-render; put the prompt back if a width still has not been chosen.
  document.addEventListener('variant:change', function () {
    Object.keys(forms).forEach(function (id) {
      if (forms[id].pending) requestAnimationFrame(function () { applyPending(id); });
    });
  });

  // Block Add to cart (button or Enter key) while no width is chosen, and show the shopper why.
  document.addEventListener('click', function (event) {
    var buyButton = event.target.closest && event.target.closest('buy-buttons');
    if (!buyButton || !event.target.closest('button[type="submit"]')) return;
    var formId = buyButton.getAttribute('form');
    if (forms[formId] && forms[formId].pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      promptForWidth(formId);
    }
  }, true);

  document.addEventListener('submit', function (event) {
    var formId = event.target && event.target.getAttribute && event.target.getAttribute('id');
    if (forms[formId] && forms[formId].pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      promptForWidth(formId);
    }
  }, true);

  window.customElements.define('wss-width-guard', WidthGuard);
})();
